import * as AgGrid from '@ag-grid-community/core'
import { format } from 'date-fns'

import {
  CONTROLS_COLUMN_ID,
  RundownColDef,
  RundownColumnType,
} from '#util/rundowns'

import { CheckboxCell, ControlsCell, SnapstreamLinkCell } from '../cells'
import { CheckboxHeader } from '../CheckboxHeader'

import * as helpers from './durationHelpers'
import { blockIdGetter, scriptIdGetter } from './idGetters'

import styles from './ColumnTypes.module.scss'

const text: AgGrid.ColDef = {
  editable: true,
  width: 300,
}

const longText: AgGrid.ColDef = {
  editable: true,
  width: 300,
  cellEditor: 'agLargeTextCellEditor',
  cellEditorPopup: true,
  cellEditorParams: {
    maxLength: 2500,
    rows: 10,
    cols: 50,
  },
}

export const buildControlsColumn = (editable: boolean): AgGrid.ColDef => ({
  colId: CONTROLS_COLUMN_ID,
  cellRenderer: ControlsCell,
  headerName: '',
  headerComponent: CheckboxHeader,
  lockPosition: 'left',
  suppressMovable: true,
  pinned: true,
  resizable: false,
  suppressColumnsToolPanel: true,
  cellClass: [styles.column___controls],
  width: editable ? 70 : 50,
  cellRendererParams: {
    showEditControls: editable,
  },
})

const duration: AgGrid.ColDef = {
  editable: true,
  // the custom parser and formatter for this field result in undefined being compared to null
  // when a user attempts to delete a blank cell or enter an invalid value with no fallback
  // this ensures that we dont trigger an update in those scenarios
  equals: (oldVal, newVal) => (oldVal ?? null) === (newVal ?? null),
  valueFormatter: helpers.durationFormatter,
  valueParser: helpers.durationParser,
  cellEditorParams: {
    useFormatter: true,
  },
  cellClass: [styles.column___rightAlign],
}

const cumulativeDuration: AgGrid.ColDef = {
  valueFormatter: helpers.durationFormatter,
  valueGetter: helpers.cumulativeGetter,
  cellClass: [styles.column___rightAlign, styles.column___readOnly],
}

const readonlyBlockId: AgGrid.ColDef = {
  editable: false,
  valueGetter: blockIdGetter,
  cellClass: [styles.column___readOnly],
}

const readonlyScriptId: AgGrid.ColDef = {
  editable: false,
  valueGetter: scriptIdGetter,
  cellClass: [styles.column___readOnly],
}

const snapstreamLink: AgGrid.ColDef = {
  cellRenderer: SnapstreamLinkCell,
  cellClass: [styles.column___snapstreamLink],
}

const frontTiming: AgGrid.ColDef = {
  valueFormatter: helpers.timeFormatter,
  valueGetter: helpers.frontTimingGetter,
  cellClass: [styles.column___readOnly, styles.column___rightAlign],
}

const endTiming: AgGrid.ColDef = {
  valueFormatter: helpers.timeFormatter,
  valueGetter: helpers.endTimingGetter,
  cellClass: [styles.column___readOnly, styles.column___rightAlign],
}

const backTiming: AgGrid.ColDef = {
  valueFormatter: helpers.timeFormatter,
  valueGetter: helpers.backTimingGetter,
  cellClass: [styles.column___readOnly, styles.column___rightAlign],
}

const itemNumber: AgGrid.ColDef = {
  editable: true,
  headerName: '#',
  width: 70,
}

const checkbox: AgGrid.ColDef = {
  cellRenderer: CheckboxCell,
}

const columnTypes: Record<RundownColumnType, AgGrid.ColDef> = {
  backTiming,
  checkbox,
  cumulativeDuration,
  duration,
  endTiming,
  frontTiming,
  itemNumber,
  snapstreamLink,
  text,
  longText,
  readonlyBlockId,
  readonlyScriptId,
}

export const schemaColumnToColDef = (
  schemaColumn: RundownColDef,
): AgGrid.ColDef & { colId: string } => {
  const { rundownColumnType, ...overrides } = schemaColumn
  const baseColDef = columnTypes[rundownColumnType]
  return {
    ...baseColDef,
    ...overrides,
    autoHeight: true,
  }
}

const maybeFormatDate = (value: unknown) => {
  if (value instanceof Date) {
    return format(value, 'yyyy-MM-dd HH:mm:ss')
  }
  return value
}

// These transform some of our internal values to strings when exporting
// or copying to clipboard
export const CELL_VALUE_EXPORTER: Partial<
  Record<RundownColumnType, (value: unknown) => unknown>
> = {
  duration: helpers.durationExporter,
  cumulativeDuration: helpers.durationExporter,
  frontTiming: maybeFormatDate,
  backTiming: maybeFormatDate,
}
